import { lazy, Suspense, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineEdit from '~icons/ic/baseline-edit'
import type { CropImageRef } from 'src/components/CropImage'
import LoadingPage from 'src/components/LoadingPage'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import UserAvatar from 'src/components/UserAvatar'
import AvatarBadge from 'src/components/UserAvatar/AvatarBadge'
import { executeUpdateMyInfo, useMyInfo } from 'src/services/apollo/auth'
import { cn, cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { uploadMedia } from 'src/utils/upload'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { userHasMembership } from 'src/utils/user'
import { openMembershipMoreCreditsModal } from '../Membership/MembershipMoreCreditsModal'
import { UIButton } from '../ui/Button'

const CropImage = lazy(() => import('src/components/CropImage'))

const EditAvatar = (
  props: {
    avatarClassName?: string
    displayBadgeRemark?: boolean
  } & StyleProps,
) => {
  const { t } = useTranslation(['user'])
  const { data: userInfo } = useMyInfo()

  const [isUploadAvatarDialogOpen, setIsUploadAvatarDialogOpen] =
    useState(false)
  const [hasImage, setHasImage] = useState(false)

  const cropImageRef = useRef<CropImageRef>(null)

  const handleClose = () => {
    setHasImage(false)
    cropImageRef.current?.reset()
    setIsUploadAvatarDialogOpen(false)
  }

  const [loading, submit] = useLoadingCallback(async () => {
    if (!cropImageRef.current) return
    const file = await cropImageRef.current.getImage()
    if (!file) return
    try {
      const media = await uploadMedia(file, { preserveFileType: true })
      if (!media) return
      await executeUpdateMyInfo({
        input: {
          avatarMediaId: media.mediaId,
        },
      })

      toast.success(`Success`)
      handleClose()
      track('edit_avatar_success')
    } catch (err) {
      toast.error(
        `Failed to upload image: ${err instanceof Error ? err.message : String(err)}`,
      )
    }
  }, [])

  const isMember = userHasMembership(userInfo)
  const allowedFileTypes = [
    'image/jpeg',
    'image/png',
    ...(isMember ? ['image/gif'] : []),
  ].join(',')

  return (
    <>
      <div className={cx('flex flex-col gap-4 items-center', props.className)}>
        <div
          className="group relative cursor-pointer"
          onClick={() => {
            setIsUploadAvatarDialogOpen(true)
            track('click_edit_avatar')
          }}
        >
          <UserAvatar
            user={userInfo}
            className={cn('size-40', props.avatarClassName)}
            decorations={{ badge: false, frame: true }}
            avatarImageQuality="thumbnail"
          />

          <div className="absolute inset-0 rounded-full bg-black/70 group-hover:opacity-100 transition opacity-0 flex justify-center items-center text-white pointer-events-none z-[1]">
            <IcBaselineEdit className="size-6" />
          </div>

          {userInfo && (
            <AvatarBadge
              user={userInfo}
              decorationBadge={true}
              /** z-[2]: Higher than EditIcon */
              className="absolute -translate-x-1/2 -translate-y-1/2 z-[2]"
              style={{
                left: 'calc(50% + 50% * sin(45deg))',
                top: 'calc(50% + 50% * sin(45deg))',
                width: '40%',
              }}
              remarkDisplayType={props.displayBadgeRemark ? 'auto' : false}
            />
          )}
        </div>
      </div>

      <UIDialog
        open={isUploadAvatarDialogOpen}
        onClose={handleClose}
        dismissable
        className="w-[calc(100vw-2rem)] max-w-screen-mmd"
      >
        <UIDialogHeader heading={t('user:change-profile-image')}>
          {({ heading }) => (
            <div className="prose dark:prose-invert">{heading}</div>
          )}
        </UIDialogHeader>
        <UIDialogContent>
          <Suspense fallback={<LoadingPage />}>
            <CropImage
              ref={cropImageRef}
              allowedFileTypes={allowedFileTypes}
              cropShape={'round'}
              onImageChange={setHasImage}
            />
          </Suspense>
        </UIDialogContent>
        <UIDialogFooter className="">
          {hasImage && (
            <UIButton
              variant="outlined"
              onPress={() => cropImageRef.current?.reset()}
            >
              {t('action:back')}
            </UIButton>
          )}
          <div className="flex-1" />
          {!isMember && (
            <button
              className="text-blue-600 text-sm"
              onClick={() => {
                openMembershipMoreCreditsModal({
                  trackingLocation: 'avatar-gifs',
                })
              }}
            >
              {t('user:profile.membership-supports-gif-image')}
            </button>
          )}
          <UIButton variant="outlined" onPress={handleClose}>
            {t('action:cancel')}
          </UIButton>
          {hasImage && (
            <UIButton variant="filled" busy={loading} onPress={submit}>
              {t('user:submit-avatar')}
            </UIButton>
          )}
        </UIDialogFooter>
      </UIDialog>
    </>
  )
}

export default EditAvatar
